/**
 * FormBuilderSection — Enhanced form renderer.
 *
 * Supports: row-based CSS grid layout, stage-based multi-step navigation,
 * localStorage persistence with expiry, restore banner, progress indicator,
 * stage summary cards, form design tokens, auth-aware submission, server-side
 * draft save/resume, and post-submit editing.
 *
 * ── Authentication & Public Form Behavior ────────────────────────────────────
 *
 * NO separate "requires auth" configuration is needed on public forms.
 * Authentication is determined purely by whether the user is already logged in
 * (userData present in localStorage from a prior sign-in).
 *
 *  Anonymous user  → submission uses publicApiCall (no Bearer token).
 *                    submission_behavior is ignored; a new row is always created.
 *                    Draft auto-save falls back to localStorage only.
 *
 *  Authenticated user → submission uses apiCall (sends Bearer token).
 *                    The backend honours submission_behavior:
 *                      always_new   — always insert a new row (default)
 *                      one_per_user — upsert a single submitted record
 *                      draft_resume — auto-save drafts to the server;
 *                                     resume from last saved stage on return
 *                      edit_existing— pre-fill existing record for editing
 *
 * Private pages: the page-level auth guard ensures the user is always logged in
 * before the form renders, so all behaviors work automatically.
 *
 * Popup form on a public page + submission_behavior that needs auth:
 *   If the user is not logged in and the behavior requires it, clicking Submit
 *   redirects to /sign-in?next=<currentPath>?open_form=<sectionUid>.
 *   After login the page reloads, the popup auto-opens (reads ?open_form),
 *   and GET /my-submission/ restores the saved draft.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { retrieve } from '../../../utils/storage_helper';
import APIHelper from '../../../api/APIHelper';
import envConfig from '../../../config/envConfig';
import LookupField from './LookupField';
import {
  readSubmitFailure, stageHoldingFirstError, successDestination, successButton, withFixedValues, isSessionGone,
  geoPointValue,
  repeatGroups, repeatedFieldIds, copyCount, setCopyValue, removeCopy,
} from './formSubmitFeedback';
import { ruleError, inputMaxLength, domainError } from './fieldRules';

const apiHelper = new APIHelper({
  apiUrl:       envConfig.backendApiUrl,
  tokenUrl:     envConfig.backendTokenUrl,
  clientId:     envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

// ── Auth helpers ──────────────────────────────────────────────────────────────

function isUserLoggedIn() {
  return !!retrieve('userData');
}

const BEHAVIORS_REQUIRING_AUTH = new Set(['one_per_user', 'draft_resume', 'edit_existing']);

/**
 * Sends the person to sign in and brings them back to this form afterwards.
 * A popup form also carries ?open_form=<sectionUid> so it reopens itself.
 *
 * Used both before submitting (when the form's behaviour needs an account) and
 * after a 401 — a bound form writes real records and the server requires a
 * signed-in submitter, so "sign in and come back" is the answer in both cases.
 */
function goToSignIn(sectionUid) {
  const next = encodeURIComponent(
    window.location.pathname + window.location.search +
    (sectionUid ? `${window.location.search ? '&' : '?'}open_form=${sectionUid}` : '')
  );
  window.location.href = `/sign-in?next=${next}`;
}

// Submit: use apiCall when logged in (sends Bearer), publicApiCall otherwise.
async function submitFormData(path, body) {
  if (isUserLoggedIn()) {
    return apiHelper.apiCall(path, { method: 'POST', body: JSON.stringify(body) });
  }
  return apiHelper.publicApiCall(path, { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Sends one file for one file field and returns what to put in the column.
 *
 * The field holds the URL; the bytes live in file storage. The request names a
 * form and a field and nothing else — where the file lands is the server's
 * decision, read from the field's own configuration.
 */
async function uploadFieldFile(formId, fieldId, file) {
  const body = new FormData();
  body.append('file', file);
  // APIHelper leaves Content-Type alone for FormData so the boundary survives.
  const resp = await apiHelper.apiCall(
    `/arcee/public/form/${formId}/upload/${fieldId}/`, { method: 'POST', body },
  );
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(payload.error || payload.detail || 'That file could not be uploaded.');
  }
  // The server answers with a path, not a URL — it reaches the storage server
  // over the container network and cannot know the address a browser would use.
  // This side does: envConfig.fileServerUrl is the browser's own base, the same
  // one the admin's uploader has always used.
  const base = String(envConfig.fileServerUrl || '').replace(/\/$/, '');
  const path = payload.path || '';
  return { ...payload, url: /^https?:\/\//i.test(path) ? path : `${base}${path}` };
}

const prettyBytes = (n) => (
  !n ? '' : n < 1024 ? `${n} B`
    : n < 1048576 ? `${(n / 1024).toFixed(0)} KB`
      : `${(n / 1048576).toFixed(1)} MB`
);

// Draft load: always requires auth — uses apiCall.
async function loadMySubmission(formId) {
  return apiHelper.apiCall(`/arcee/public/form/${formId}/my-submission/`);
}

// The record a bound form previously created for this person, for editing.
// Which record is "theirs" is decided by the server from the session — the
// request cannot name one.
async function loadMyRecord(formId) {
  return apiHelper.apiCall(`/arcee/public/form/${formId}/record/`);
}

/** A form that writes real table rows, rather than only recording answers. */
function isBoundForm(cfg) {
  return ((cfg || {}).operations || []).length > 0;
}

// Draft save: PATCH /my-submission/ — requires auth.
async function saveMyDraft(formId, data, stageReached) {
  return apiHelper.apiCall(`/arcee/public/form/${formId}/my-submission/`, {
    method: 'PATCH',
    body: JSON.stringify({ data, stage_reached: stageReached }),
  });
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const DEFAULT_TOKENS = {
  inputBgColor: '#ffffff', inputBgColorFocus: '#ffffff',
  inputBorderColor: '#e2e8f0', inputBorderColorFocus: '#4e2e80',
  inputBorderColorError: '#dc2626',
  inputBorderWidth: 1, inputBorderStyle: 'solid', inputBorderRadius: 6,
  inputPaddingX: 12, inputPaddingY: 8, inputFontSize: 14,
  inputFontFamily: '', inputFontWeight: 400,
  inputFontStyle: 'normal', inputTextAlign: 'left',
  inputTextColor: '#1e293b', inputPlaceholderColor: '#94a3b8',
  inputPlaceholderFontSize: 0, inputPlaceholderFontWeight: 0,
  inputPlaceholderFontStyle: 'normal', inputPlaceholderFontFamily: '',
  inputHeight: 38, inputShadow: 'none', inputShadowFocus: 'ring',
  inputFocusRingColor: '#4e2e80', inputFocusRingWidth: 2, inputTransitionDuration: 150,
  labelFontSize: 13, labelFontWeight: 600, labelColor: '#374151',
  labelFontFamily: '', labelFontStyle: 'normal', labelTextAlign: 'left',
  labelPosition: 'top', labelMarginBottom: 4, requiredMarkColor: '#dc2626',
  helpTextFontSize: 11, helpTextColor: '#64748b',
  errorFontSize: 11, errorColor: '#dc2626', errorIconVisible: true,
  successColor: '#16a34a', successIconVisible: true,
  submitBgColor: '#4e2e80', submitBgColorHover: '#3b1f6a',
  submitTextColor: '#ffffff', submitBorderRadius: 8,
  submitPaddingX: 24, submitPaddingY: 10,
  submitFontSize: 14, submitFontWeight: 600,
  submitShadow: 'none', submitFullWidth: false,
  navButtonBgColor: '#f1f5f9', navButtonHoverBgColor: '#e2e8f0',
  navButtonTextColor: '#374151', navButtonBorderRadius: 8,
  navButtonStyle: 'filled', navButtonFontSize: 13,
  navButtonFontWeight: 500, navButtonPaddingX: 18, navButtonPaddingY: 8,
  cancelButtonBgColor: 'transparent', cancelButtonTextColor: '#64748b',
  cancelButtonBorderRadius: 8, cancelButtonBorderColor: '#e2e8f0',
  cancelButtonStyle: 'ghost', cancelButtonFontSize: 13,
  cancelButtonFontWeight: 500, cancelButtonPaddingX: 16, cancelButtonPaddingY: 8,
  formBgColor: '#ffffff', formBgOpacity: 100,
  formBorderVisible: false, formBorderColor: '#e2e8f0',
  formBorderWidth: 1, formBorderRadius: 12, formBorderStyle: 'solid',
  formPaddingX: 32, formPaddingY: 32,
  formShadow: 'none', formMaxWidth: 600, fieldGap: 16, rowGap: 24,
  progressFontSize: 12, progressFontWeight: 500,
  progressBgColor: '#f8fafc', progressPadding: 16, progressBorderRadius: 8,
  restoreBannerBgColor: '#eff6ff', restoreBannerTextColor: '#1d4ed8',
  restoreBannerBorderColor: '#bfdbfe', restoreBannerBorderRadius: 8,
  summarySectionLabelColor: '#6b7280', summaryValueColor: '#1e293b',
  summaryEditButtonColor: '#4e2e80',
};

const PRESET_DESIGN_TOKENS = {
  default: { ...DEFAULT_TOKENS },
  minimal: {
    ...DEFAULT_TOKENS, inputBgColor: '#f8fafc', inputBorderColor: 'transparent',
    inputBorderWidth: 0, inputBorderRadius: 0, inputShadow: 'none', inputShadowFocus: 'none',
    formBgColor: '#f8fafc', formShadow: 'none', labelColor: '#64748b', labelFontWeight: 500,
    submitBgColor: '#1e293b',
  },
  rounded: {
    ...DEFAULT_TOKENS, inputBorderRadius: 12, inputBorderColorFocus: '#8b5cf6',
    inputFocusRingColor: '#8b5cf6', inputShadow: 'sm',
    formBorderRadius: 20, formShadow: 'md', formBorderVisible: true, formBorderColor: '#f3e8ff',
    submitBgColor: '#8b5cf6', submitBorderRadius: 12, navButtonBorderRadius: 12,
  },
  bold: {
    ...DEFAULT_TOKENS, formBgColor: '#1e293b', formShadow: 'lg', labelColor: '#e2e8f0', labelFontWeight: 700,
    inputBgColor: '#0f172a', inputTextColor: '#f1f5f9', inputPlaceholderColor: '#475569',
    inputBorderColor: '#334155', inputBorderColorFocus: '#f59e0b', inputFocusRingColor: '#f59e0b', inputBorderRadius: 4,
    submitBgColor: '#f59e0b', submitTextColor: '#1e293b', submitBorderRadius: 4, submitFontWeight: 700,
    navButtonBgColor: '#334155', navButtonTextColor: '#e2e8f0',
  },
  material: {
    ...DEFAULT_TOKENS, labelPosition: 'floating',
    inputBorderWidth: 0, inputBorderRadius: 0, inputShadow: 'none', inputShadowFocus: 'none',
    formBgColor: '#ffffff', formShadow: 'none',
    submitBgColor: '#1976d2', submitBorderRadius: 4,
    navButtonBgColor: 'transparent', navButtonTextColor: '#1976d2', navButtonStyle: 'ghost',
  },
  card: {
    ...DEFAULT_TOKENS, fieldGap: 20, formPaddingX: 16, formPaddingY: 16,
    inputFontSize: 16, labelFontSize: 14,
    inputPaddingX: 16, inputPaddingY: 12, inputHeight: 48, inputBorderRadius: 8, inputShadow: 'sm',
    formBgColor: '#f8fafc', submitBgColor: '#0f766e', submitBorderRadius: 8, submitFullWidth: true,
  },
};

function resolveTokens(cfg) {
  const id = cfg.activeDesignId || 'default';
  const preset = PRESET_DESIGN_TOKENS[id] || PRESET_DESIGN_TOKENS.default;
  const custom = (cfg.designs || []).find(d => d.designId === id);
  const base = { ...DEFAULT_TOKENS, ...preset, ...(custom ? custom.tokens : {}) };

  // cfg-level overrides (Container Styling accordion + Button Configuration)
  if (cfg.cancelStyle)                    base.cancelButtonStyle  = cfg.cancelStyle;

  // Form card — set by Container Styling accordion, override design tokens
  if (cfg.form_bg_color)                  base.formBgColor        = cfg.form_bg_color;
  if (cfg.form_bg_opacity !== undefined)  base.formBgOpacity      = cfg.form_bg_opacity;
  if (cfg.form_border_visible !== undefined) base.formBorderVisible = cfg.form_border_visible;
  if (cfg.form_border_color)              base.formBorderColor    = cfg.form_border_color;
  if (cfg.form_border_width !== undefined) base.formBorderWidth   = cfg.form_border_width;
  if (cfg.form_border_radius !== undefined) base.formBorderRadius = cfg.form_border_radius;
  if (cfg.form_border_style)              base.formBorderStyle    = cfg.form_border_style;
  if (cfg.form_shadow)                    base.formShadow         = cfg.form_shadow;
  if (cfg.form_padding_x !== undefined)   base.formPaddingX       = cfg.form_padding_x;
  if (cfg.form_padding_y !== undefined)   base.formPaddingY       = cfg.form_padding_y;
  if (cfg.form_max_width !== undefined)   base.formMaxWidth       = cfg.form_max_width;
  if (cfg.field_gap !== undefined)        base.fieldGap           = cfg.field_gap;
  if (cfg.row_gap !== undefined)          base.rowGap             = cfg.row_gap;

  return base;
}

// ─── Container helpers ────────────────────────────────────────────────────────

const PADDING_MAP = { none: '0', sm: '0.5rem 0.75rem', md: '1rem 1.5rem', lg: '2rem 2.5rem', xl: '3rem 3.5rem' };
const WIDTH_MAP   = { narrow: 400, normal: 600, wide: 800, full: '100%' };
const SHADOW_MAP  = { none: 'none', sm: '0 1px 6px rgba(0,0,0,0.07)', md: '0 4px 20px rgba(0,0,0,0.10)', lg: '0 8px 40px rgba(0,0,0,0.14)' };

function resolveOuterPadding(cfg) {
  if (cfg.padding === 'custom') {
    const y = cfg.section_padding_y ?? 0;
    const x = cfg.section_padding_x ?? 0;
    return `${y}px ${x}px`;
  }
  return PADDING_MAP[cfg.padding] || '0';
}

function resolveOuterMaxWidth(cfg) {
  if (cfg.form_width === 'custom') {
    return cfg.section_max_width ? `${cfg.section_max_width}px` : '100%';
  }
  const v = WIDTH_MAP[cfg.form_width];
  return v === undefined ? '100%' : (typeof v === 'number' ? `${v}px` : v);
}

function outerContainerStyle(cfg) {
  const bw = cfg.container_border_width ?? 0;
  return {
    background:   cfg.bg_color || 'transparent',
    color:        cfg.text_color || undefined,
    padding:      resolveOuterPadding(cfg),
    width:        '100%',
    maxWidth:     resolveOuterMaxWidth(cfg),
    margin:       '0 auto',
    border:       bw > 0 && cfg.container_border_color
                    ? `${bw}px ${cfg.container_border_style || 'solid'} ${cfg.container_border_color}`
                    : undefined,
    borderRadius: cfg.container_border_radius || undefined,
    boxShadow:    SHADOW_MAP[cfg.container_shadow] || undefined,
  };
}

function formCardStyle(cfg, t) {
  const opacity = t.formBgOpacity ?? 100;
  const bg = opacity < 100
    ? `rgba(${hexToRgbStr(t.formBgColor)},${opacity / 100})`
    : (t.formBgColor || 'transparent');
  const border = t.formBorderVisible
    ? `${t.formBorderWidth ?? 1}px ${t.formBorderStyle || 'solid'} ${t.formBorderColor}`
    : 'none';
  return {
    background:   bg,
    borderRadius: t.formBorderRadius,
    padding:      `${t.formPaddingY ?? 24}px ${t.formPaddingX ?? 24}px`,
    maxWidth:     t.formMaxWidth || '100%',
    width:        '100%',
    margin:       '0 auto',
    border,
    boxShadow:    shadowVal(t.formShadow),
  };
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

const LS_KEY = (key, suffix) => `${key}_${suffix}`;

function lsGet(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch (_) { return null; }
}

function lsSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
}

function lsRemove(...keys) {
  try { keys.forEach(k => localStorage.removeItem(k)); } catch (_) {}
}

function loadSavedProgress(cfg) {
  const key = cfg.persistenceKey || `form_${String(cfg.form_id || 'x').slice(-8)}`;
  const ts = lsGet(LS_KEY(key, 'timestamp'));
  if (ts && cfg.persistenceExpiry > 0) {
    const ageMs = Date.now() - ts;
    if (ageMs > cfg.persistenceExpiry * 3600000) {
      lsRemove(LS_KEY(key, 'data'), LS_KEY(key, 'stage'), LS_KEY(key, 'timestamp'));
      return null;
    }
  }
  const data  = lsGet(LS_KEY(key, 'data'));
  const stage = lsGet(LS_KEY(key, 'stage'));
  if (!data && stage == null) return null;
  return { data: data || {}, stage: stage ?? 0 };
}

function saveProgress(cfg, values, stageIndex) {
  const key = cfg.persistenceKey || `form_${String(cfg.form_id || 'x').slice(-8)}`;
  const toSave = cfg.persistenceFields === 'all' ? values
    : Object.fromEntries(Object.entries(values).filter(([id]) => (cfg.persistenceFields || []).includes(id)));
  lsSet(LS_KEY(key, 'data'), toSave);
  lsSet(LS_KEY(key, 'stage'), stageIndex);
  lsSet(LS_KEY(key, 'timestamp'), Date.now());
}

function clearProgress(cfg) {
  const key = cfg.persistenceKey || `form_${String(cfg.form_id || 'x').slice(-8)}`;
  lsRemove(LS_KEY(key, 'data'), LS_KEY(key, 'stage'), LS_KEY(key, 'timestamp'));
}

// ─── Collect all fields from any config shape ─────────────────────────────────

function getFieldsFromRows(rows) {
  return (rows || []).flatMap(row => row.fields || []);
}

function getAllFields(cfg) {
  if (cfg.stagesEnabled && (cfg.stages || []).length > 0) {
    return cfg.stages.flatMap(s => getFieldsFromRows(s.rows));
  }
  if (cfg.rowLayoutEnabled && (cfg.rows || []).length > 0) {
    return getFieldsFromRows(cfg.rows);
  }
  return cfg.fields || [];
}

// ─── Shadow helper ────────────────────────────────────────────────────────────

function shadowVal(s) {
  if (s === 'sm') return '0 1px 4px rgba(0,0,0,0.07)';
  if (s === 'md') return '0 4px 16px rgba(0,0,0,0.10)';
  if (s === 'lg') return '0 8px 32px rgba(0,0,0,0.14)';
  return 'none';
}

// ─── Field renderer ───────────────────────────────────────────────────────────

function FieldInput({ field, value, onChange, tokens, error, isMobile, formId, values, locked: lockedByServer }) {
  // A read-only field shows its fixed value and cannot be edited, the same
  // way a field the server has locked is shown.
  const locked = lockedByServer || field.readOnly === true;
  const t = tokens;
  const [focused, setFocused] = useState(false);
  // Held per field, so revealing a password does not reveal the confirmation
  // beside it — checking that two entries match is the one thing the pair is
  // for, and a single shared toggle would let a typo hide in the other box.
  const [revealed, setRevealed] = useState(false);

  // A file field uploads as soon as one is chosen, so what it holds is a URL.
  // The name and size are kept only to show the person what they attached —
  // after a reload the name is read back out of the URL instead.
  const [upload, setUpload] = useState({ status: 'idle' });
  const startUpload = async (file) => {
    setUpload({ status: 'uploading', name: file.name, size: file.size });
    try {
      const stored = await uploadFieldFile(formId, field.id, file);
      setUpload({ status: 'done', name: stored.name || file.name, size: stored.size || file.size });
      onChange(field.id, stored.url);
    } catch (err) {
      setUpload({ status: 'error', message: err.message, name: file.name });
      onChange(field.id, '');
    }
  };

  const isFloating = t.labelPosition === 'floating';
  const isMaterialStyle = t.inputBorderWidth === 0;

  // Border is set with longhand properties only, never the `border` shorthand.
  // Mixing the two leaves a stale edge behind: React cannot reliably clear a
  // longhand it set on a previous render when the shorthand changes, so an
  // underlined material input kept its bottom rule after switching styles, and
  // every focus change warned about the conflict.
  const activeBorderColor = error
    ? t.inputBorderColorError
    : focused ? t.inputBorderColorFocus : t.inputBorderColor;

  const baseInputStyle = {
    borderWidth: isMaterialStyle ? '0 0 2px 0' : `${t.inputBorderWidth}px`,
    borderStyle: isMaterialStyle ? 'solid' : t.inputBorderStyle,
    borderColor: activeBorderColor,
    borderRadius: isMaterialStyle ? 0 : t.inputBorderRadius,
    padding: `${t.inputPaddingY}px ${t.inputPaddingX}px`,
    fontSize: t.inputFontSize,
    fontFamily: t.inputFontFamily || 'inherit',
    fontWeight: t.inputFontWeight,
    fontStyle: t.inputFontStyle || 'normal',
    textAlign: t.inputTextAlign || 'left',
    color: t.inputTextColor,
    width: '100%',
    height: field.type === 'textarea' ? 'auto' : t.inputHeight,
    outline: focused && t.inputShadowFocus === 'ring'
      ? `${t.inputFocusRingWidth}px solid ${t.inputFocusRingColor}`
      : 'none',
    boxShadow: focused && t.inputShadowFocus === 'glow'
      ? `0 0 0 3px ${t.inputFocusRingColor}40`
      : t.inputShadow === 'sm' ? '0 1px 4px rgba(0,0,0,0.07)'
      : t.inputShadow === 'md' ? '0 4px 8px rgba(0,0,0,0.10)' : 'none',
    transition: `all ${t.inputTransitionDuration}ms`,
    boxSizing: 'border-box',
    background: isMaterialStyle ? 'transparent' : (focused ? t.inputBgColorFocus : t.inputBgColor),
  };

  const commonProps = {
    value: value || '',
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    onChange: e => onChange(field.id, e.target.value),
    required: field.required,
    disabled: locked,
    id: `field_${field.id}`,
    placeholder: isFloating ? '' : (field.placeholder || ''),
    style: baseInputStyle,
    maxLength: inputMaxLength(field),
  };

  // A lookup carries its own fetching, debouncing and modal, so it lives in
  // its own component rather than in this switch.
  if (field.type === 'lookup') {
    return (
      <LookupField field={field} value={value} onChange={onChange} tokens={t}
        error={error} formId={formId} values={values} locked={locked} />
    );
  }

  // A pair of numbers, not text: the column stores a point, and the platform
  // accepts {lat, lng}. Held as one value so a half-filled pair never counts as
  // an answer — a latitude with no longitude is not a place.
  if (field.type === 'geo_point') {
    const point = (value && typeof value === 'object') ? value : {};
    const setPart = (part, raw) => onChange(field.id, geoPointValue(point, part, raw));
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        <input {...commonProps} type="number" step="any" id={`field_${field.id}_lat`}
          placeholder="Latitude" value={point.lat === undefined ? '' : point.lat}
          onChange={e => setPart('lat', e.target.value)} />
        <input {...commonProps} type="number" step="any" id={`field_${field.id}_lng`}
          placeholder="Longitude" value={point.lng === undefined ? '' : point.lng}
          onChange={e => setPart('lng', e.target.value)} />
      </div>
    );
  }

  let input;
  switch (field.type) {
    case 'datetime':
      // datetime-local yields 'YYYY-MM-DDTHH:MM', which a datetime column takes
      // as-is. No zone is attached, so it means the wall clock the person typed.
      input = <input {...commonProps} type="datetime-local" />;
      break;
    case 'textarea':
      input = <textarea {...commonProps} rows={4} style={{ ...baseInputStyle, height: 'auto', resize: 'vertical' }} />;
      break;
    case 'select':
      input = (
        <select {...commonProps} style={{ ...baseInputStyle, cursor: 'pointer' }}>
          <option value="">Select…</option>
          {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
      break;
    case 'checkbox':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" id={`field_${field.id}`} checked={!!value} disabled={locked}
            onChange={e => onChange(field.id, e.target.checked)} style={{ width: 16, height: 16 }} />
          <label htmlFor={`field_${field.id}`} style={{ fontSize: t.labelFontSize, color: t.labelColor, cursor: 'pointer' }}>
            {field.label}{field.required && <span style={{ color: t.requiredMarkColor }}> *</span>}
          </label>
        </div>
      );
    case 'radio':
      return (
        <div>
          {(field.options || []).map(opt => (
            <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <input type="radio" name={`field_${field.id}`} id={`${field.id}_${opt}`} disabled={locked}
                value={opt} checked={value === opt} onChange={e => onChange(field.id, e.target.value)} />
              <label htmlFor={`${field.id}_${opt}`} style={{ fontSize: t.labelFontSize - 1, color: t.labelColor, cursor: 'pointer' }}>{opt}</label>
            </div>
          ))}
        </div>
      );
    // A file field holds a URL, not the bytes. Choosing a file uploads it
    // straight away and keeps the URL it comes back with, so by the time the
    // form is submitted the value is already a real, stored file. A raw
    // <input type="file"> cannot do this: its value is a fake local path, which
    // is what used to get written into the column.
    case 'file': {
      const inputId = `field_${field.id}_picker`;
      const kind = (field.file || {}).accept || 'document';
      // The builder can say what a good file looks like — dimensions, a sensible
      // weight — which the accepted-types line cannot. A hard limit is not the
      // same as a recommendation, and people need the second one more.
      const hint = (field.file || {}).hint || (
        kind === 'image' ? 'PNG, SVG or JPG · up to 4 MB' : 'PDF or image · up to 8 MB'
      );
      const done = !!value;
      const busy = upload.status === 'uploading';
      const failed = upload.status === 'error';
      const edge = failed ? t.inputBorderColorError
        : done ? t.successColor || '#12886a'
          : t.inputBorderColor;

      input = (
        <div>
          <input
            id={inputId} type="file" disabled={locked || busy}
            accept={kind === 'image' ? 'image/*' : 'image/*,application/pdf'}
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files && e.target.files[0];
              e.target.value = '';           // same file twice must re-trigger
              if (file) startUpload(file);
            }}
          />
          <label
            htmlFor={busy || locked ? undefined : inputId}
            style={{
              ...baseInputStyle,
              height: 'auto', borderColor: edge,
              borderStyle: done ? 'solid' : 'dashed',
              display: 'flex', alignItems: 'center', gap: 12,
              padding: `${Math.max(t.inputPaddingY, 10)}px ${t.inputPaddingX}px`,
              cursor: busy || locked ? 'default' : 'pointer',
              margin: 0,
            }}
          >
            <span style={{
              width: 34, height: 34, flex: 'none', borderRadius: 9,
              display: 'grid', placeItems: 'center', fontSize: 15,
              background: `${done ? (t.successColor || '#12886a') : t.inputBorderColorFocus}1f`,
              color: done ? (t.successColor || '#12886a') : t.inputBorderColorFocus,
            }}>
              <i className={`bi ${busy ? 'bi-arrow-repeat' : done ? 'bi-check2' : 'bi-paperclip'}`}
                style={busy ? { animation: 'arcee-spin 900ms linear infinite' } : undefined} />
            </span>

            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{
                display: 'block', fontSize: 13.5, fontWeight: 600, color: t.inputTextColor,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {busy ? `Uploading ${upload.name}…`
                  : done ? (upload.name || decodeURIComponent(String(value).split('/').pop()))
                    : 'Choose a file'}
              </span>
              <span style={{ display: 'block', fontSize: 11.5, color: t.helpTextColor }}>
                {busy ? 'Please wait'
                  : done ? `${prettyBytes(upload.size)}${upload.size ? ' · ' : ''}Click to replace`
                    : hint}
              </span>
            </span>

            {done && !busy && (
              <button
                type="button" disabled={locked}
                onClick={e => { e.preventDefault(); setUpload({ status: 'idle' }); onChange(field.id, ''); }}
                style={{
                  flex: 'none', background: 'none', border: 'none', cursor: 'pointer',
                  color: t.helpTextColor, fontSize: 15, lineHeight: 1, padding: 4,
                }}
                aria-label="Remove file"
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
          </label>
          {failed && (
            <div style={{ color: t.errorColor, fontSize: t.errorFontSize, marginTop: 6 }}>
              {upload.message}
            </div>
          )}
        </div>
      );
      break;
    }
    case 'password': {
      // Room for the button, so a long password never runs underneath it.
      const gutter = (t.inputPaddingX || 12) + 30;
      input = (
        <div style={{ position: 'relative' }}>
          <input {...commonProps} type={revealed ? 'text' : 'password'}
            style={{ ...baseInputStyle, paddingRight: gutter }} />
          <button
            type="button"
            onClick={() => setRevealed(v => !v)}
            disabled={locked}
            aria-controls={`field_${field.id}`}
            aria-pressed={revealed}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            title={revealed ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', top: 0, bottom: 0, right: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: gutter, padding: 0, border: 'none', background: 'none',
              color: t.helpTextColor || t.labelColor,
              cursor: locked ? 'not-allowed' : 'pointer',
              opacity: locked ? 0.5 : 1,
              fontSize: (t.inputFontSize || 14) + 1, lineHeight: 0,
            }}>
            <i className={revealed ? 'bi bi-eye-slash' : 'bi bi-eye'} aria-hidden="true" />
          </button>
        </div>
      );
      break;
    }
    default:
      input = <input {...commonProps} type={field.type || 'text'} />;
  }

  if (isFloating && field.type !== 'checkbox' && field.type !== 'radio') {
    const hasValue = value && String(value).length > 0;
    return (
      <div style={{ position: 'relative', paddingTop: 14 }}>
        {input}
        <label htmlFor={`field_${field.id}`} style={{
          position: 'absolute',
          top: focused || hasValue ? 0 : '50%',
          left: t.inputPaddingX,
          transform: focused || hasValue ? 'none' : 'translateY(-50%)',
          fontSize: focused || hasValue ? t.labelFontSize - 2 : t.labelFontSize,
          color: focused ? t.inputBorderColorFocus : t.labelColor,
          fontWeight: t.labelFontWeight,
          transition: `all ${t.inputTransitionDuration}ms`,
          pointerEvents: 'none',
          background: t.inputBgColor,
          padding: '0 2px',
        }}>
          {field.label}{field.required && <span style={{ color: t.requiredMarkColor }}> *</span>}
        </label>
      </div>
    );
  }

  return input;
}

function FormField({ field, value, onChange, tokens, error, formId, values, locked }) {
  const t = tokens;
  if (field.type === 'checkbox' || field.type === 'radio') {
    return (
      <div style={{ marginBottom: 0 }}>
        <FieldInput field={field} value={value} onChange={onChange} tokens={t} error={error} formId={formId} values={values} locked={locked} />
        {error && (
          <div style={{ fontSize: t.errorFontSize, color: t.errorColor, marginTop: 3 }}>
            {t.errorIconVisible && <i className="bi bi-exclamation-circle me-1" />}{error}
          </div>
        )}
        {field.helpText && <div style={{ fontSize: t.helpTextFontSize, color: t.helpTextColor, marginTop: 3 }}>{field.helpText}</div>}
      </div>
    );
  }

  const isFloating = t.labelPosition === 'floating';

  return (
    <div style={{ marginBottom: 0 }}>
      {!isFloating && (
        <label htmlFor={`field_${field.id}`} style={{
          display: 'block', fontSize: t.labelFontSize, fontWeight: t.labelFontWeight,
          fontFamily: t.labelFontFamily || 'inherit', fontStyle: t.labelFontStyle || 'normal',
          textAlign: t.labelTextAlign || 'left',
          color: t.labelColor, marginBottom: t.labelMarginBottom,
        }}>
          {field.label}{field.required && <span style={{ color: t.requiredMarkColor }}> *</span>}
        </label>
      )}
      <FieldInput field={field} value={value} onChange={onChange} tokens={t} error={error} formId={formId} values={values} locked={locked} />
      {error && (
        <div style={{ fontSize: t.errorFontSize, color: t.errorColor, marginTop: 4 }}>
          {t.errorIconVisible && <i className="bi bi-exclamation-circle me-1" />}{error}
        </div>
      )}
      {field.helpText && !error && (
        <div style={{ fontSize: t.helpTextFontSize, color: t.helpTextColor, marginTop: 4 }}>{field.helpText}</div>
      )}
    </div>
  );
}

// ─── Row grid ─────────────────────────────────────────────────────────────────

function RowGrid({ rows, values, onChange, errors, tokens, formId, lockedFields }) {
  const t = tokens;
  return (
    <div>
      {(rows || []).map((row, ri) => {
        const cols = row.fieldsPerRow || 1;
        return (
          <div key={row.rowId || ri} style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: `${t.fieldGap}px ${row.gap || 12}px`,
            marginBottom: row.verticalSpacing || t.fieldGap,
          }}>
            {(row.fields || []).map(field => (
              <div key={field.id} style={{
                gridColumn: field.fullWidth ? '1 / -1' : field.columnSpan > 1 ? `span ${Math.min(field.columnSpan, cols)}` : 'span 1',
              }}>
                <FormField field={field} value={values[field.id]} onChange={onChange} tokens={t} error={errors[field.id]} formId={formId} values={values} locked={(lockedFields || []).includes(field.id)} />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A block of questions the person can add more of.
 *
 * Each copy's answers live at the same index across every field of the block,
 * so removing a copy removes that index everywhere — otherwise the second
 * activity's name would end up beside the third activity's type.
 */
function RepeatBlock({ group, fields, values, onCopyChange, onRemoveCopy, onAddCopy,
                       errors, tokens, formId }) {
  const t = tokens;
  const copies = copyCount(group, values);
  const noun = group.label || 'another';

  return (
    <div style={{ marginBottom: t.rowGap || 24 }}>
      {Array.from({ length: copies }).map((_, index) => (
        <div key={index} style={{
          border: `1px solid ${t.inputBorderColor}`,
          borderRadius: t.inputBorderRadius,
          padding: '12px 14px',
          marginBottom: 10,
          position: 'relative',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 8,
          }}>
            <span style={{
              fontSize: t.helpTextFontSize + 1, color: t.helpTextColor,
              fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase',
            }}>
              {noun === 'another' ? `#${index + 1}` : `${noun} ${index + 1}`}
            </span>
            {copies > 1 && (
              <button type="button" onClick={() => onRemoveCopy(group, index)}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  color: t.errorColor, fontSize: t.helpTextFontSize + 1,
                }}>
                Remove
              </button>
            )}
          </div>

          {fields.map(field => (
            <div key={field.id} style={{ marginBottom: t.fieldGap }}>
              <FormField
                field={field}
                value={Array.isArray(values[field.id]) ? values[field.id][index] : ''}
                onChange={(fieldId, value) => onCopyChange(fieldId, index, value)}
                tokens={t}
                error={errors[`${field.id}#${index}`]}
                formId={formId}
                values={values}
              />
            </div>
          ))}
        </div>
      ))}

      <button type="button" onClick={() => onAddCopy(group)}
        style={{
          border: `1px dashed ${t.inputBorderColor}`,
          background: 'transparent',
          color: t.inputFocusRingColor,
          borderRadius: t.inputBorderRadius,
          padding: '8px 0', width: '100%', cursor: 'pointer',
          fontSize: t.inputFontSize, fontWeight: 600,
        }}>
        + Add {noun}
      </button>
    </div>
  );
}

// ─── Progress indicator ───────────────────────────────────────────────────────

function ProgressIndicator({ stages, currentStage, cfg, tokens, completedStages, onJump }) {
  const t = tokens;
  const style = cfg.progressIndicatorStyle || 'steps';
  if (style === 'none') return null;

  const activeColor   = cfg.progressActiveColor   || 'var(--primary-color)';
  const completeColor = cfg.progressCompleteColor || '#16a34a';
  const inactiveColor = cfg.progressInactiveColor || '#e2e8f0';

  const wrapStyle = {
    padding: `${t.progressPadding}px`,
    background: t.progressBgColor,
    borderRadius: t.progressBorderRadius,
    marginBottom: 24,
  };

  if (style === 'bar') {
    const pct = Math.round(((currentStage) / stages.length) * 100);
    return (
      <div style={wrapStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: t.progressFontSize, fontWeight: t.progressFontWeight, color: '#374151' }}>
            {stages[currentStage]?.stageLabel || `Step ${currentStage + 1}`}
          </span>
          <span style={{ fontSize: t.progressFontSize, color: '#64748b' }}>{currentStage + 1} / {stages.length}</span>
        </div>
        <div style={{ height: 5, background: inactiveColor, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            height: '100%', background: activeColor, width: `${pct}%`,
            borderRadius: 3, transition: cfg.progressAnimated !== false ? 'width 0.35s ease' : 'none',
          }} />
        </div>
      </div>
    );
  }

  if (style === 'dots') {
    return (
      <div style={{ ...wrapStyle, display: 'flex', justifyContent: 'center', gap: 8, alignItems: 'center' }}>
        {stages.map((_, i) => (
          <div key={i} onClick={() => cfg.progressClickable && completedStages.has(i) && onJump(i)}
            style={{
              width: i === currentStage ? 22 : 8, height: 8, borderRadius: 4,
              background: i === currentStage ? activeColor : completedStages.has(i) ? completeColor : inactiveColor,
              transition: cfg.progressAnimated !== false ? 'all 0.3s ease' : 'none',
              cursor: cfg.progressClickable && completedStages.has(i) ? 'pointer' : 'default',
            }} />
        ))}
      </div>
    );
  }

  // steps / numbers / breadcrumb
  return (
    <div style={{ ...wrapStyle, display: 'flex', alignItems: 'center', overflowX: 'auto' }}>
      {stages.map((stage, i) => {
        const isActive   = i === currentStage;
        const isComplete = completedStages.has(i);
        const dotColor   = isActive ? activeColor : isComplete ? completeColor : inactiveColor;
        const textColor  = isActive || isComplete ? '#fff' : '#94a3b8';
        return (
          <React.Fragment key={stage.stageId || i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flexShrink: 0 }}
              onClick={() => cfg.progressClickable && isComplete && onJump(i)}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%', background: dotColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, color: textColor,
                cursor: cfg.progressClickable && isComplete ? 'pointer' : 'default',
                transition: cfg.progressAnimated !== false ? 'background 0.3s ease' : 'none',
                boxShadow: isActive ? `0 0 0 4px ${activeColor}30` : 'none',
              }}>
                {isComplete
                  ? <i className="bi bi-check" />
                  : style === 'numbers' ? i + 1 : (stage.stageIcon ? <i className={`bi bi-${stage.stageIcon}`} /> : i + 1)}
              </div>
              {cfg.progressShowLabels !== false && (
                <span style={{
                  fontSize: t.progressFontSize - 1, fontWeight: t.progressFontWeight,
                  color: isActive ? '#1e293b' : '#64748b', whiteSpace: 'nowrap', maxWidth: 80,
                  overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{stage.stageLabel}</span>
              )}
            </div>
            {i < stages.length - 1 && cfg.progressConnectorVisible !== false && (
              <div style={{
                flex: 1, height: 2,
                background: completedStages.has(i) ? completeColor : inactiveColor,
                borderTop: cfg.progressConnectorStyle === 'dashed' ? `2px dashed ${completedStages.has(i) ? completeColor : inactiveColor}` : undefined,
                margin: cfg.progressShowLabels !== false ? '0 4px 22px' : '0 4px',
                transition: cfg.progressAnimated !== false ? 'background 0.3s ease' : 'none',
                minWidth: 12,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Restore banner ───────────────────────────────────────────────────────────

function RestoreBanner({ cfg, tokens, onContinue, onStartOver }) {
  const t = tokens;
  return (
    <div style={{
      background: t.restoreBannerBgColor, color: t.restoreBannerTextColor,
      border: `1px solid ${t.restoreBannerBorderColor}`,
      borderRadius: t.restoreBannerBorderRadius,
      padding: '12px 16px', marginBottom: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className="bi bi-clock-history" style={{ fontSize: 16 }} />
        <span style={{ fontSize: 13 }}>{cfg.restoreBannerText || 'You have a saved draft. Continue where you left off?'}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button type="button" onClick={onContinue} style={{
          background: t.restoreBannerTextColor, color: t.restoreBannerBgColor,
          border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>{cfg.restoreBannerContinueLabel || 'Continue'}</button>
        <button type="button" onClick={onStartOver} style={{
          background: 'transparent', color: t.restoreBannerTextColor,
          border: `1px solid ${t.restoreBannerBorderColor}`, borderRadius: 6,
          padding: '5px 14px', fontSize: 12, cursor: 'pointer',
        }}>{cfg.restoreBannerStartOverLabel || 'Start over'}</button>
      </div>
    </div>
  );
}

// ─── Stage summary card ───────────────────────────────────────────────────────

function StageSummaryCard({ stage, values, cfg, tokens, onEdit }) {
  const t = tokens;
  const fields = getFieldsFromRows(stage.rows);
  if (!fields.length) return null;

  return (
    <div style={{
      background: cfg.summaryCardBgColor || '#f8fafc',
      border: `1px solid ${cfg.summaryCardBorderColor || '#e2e8f0'}`,
      borderRadius: cfg.summaryCardBorderRadius || 8,
      padding: '12px 16px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: t.summarySectionLabelColor }}>
          <i className="bi bi-check-circle-fill me-1" style={{ color: '#16a34a' }} />
          {stage.stageLabel}
        </span>
        {cfg.summaryEditButtonVisible !== false && onEdit && (
          <button type="button" onClick={onEdit} style={{
            background: 'none', border: 'none', color: t.summaryEditButtonColor || 'var(--primary-color)',
            fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0,
          }}>
            <i className="bi bi-pencil me-1" />{cfg.summaryEditLabel || 'Edit'}
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', alignItems: 'start' }}>
        {fields.map(field => {
          const val = values[field.id];
          if (val === undefined || val === null || val === '') return null;
          const masked = field.isSensitive && cfg.summaryMaskSensitiveFields !== false;
          const displayVal = masked ? '••••••'
            : field.type === 'checkbox' ? (val ? 'Yes' : 'No')
            : Array.isArray(val) ? val.join(', ') : String(val);
          return (
            <React.Fragment key={field.id}>
              <span style={{ fontSize: 11, color: t.summarySectionLabelColor, fontWeight: 500 }}>{field.label}:</span>
              <span style={{ fontSize: 11, color: t.summaryValueColor }}>{displayVal}</span>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── Nav button ───────────────────────────────────────────────────────────────

function NavBtn({ label, onClick, tokens, variant = 'nav', disabled, style: extra, type = 'button' }) {
  const t = tokens;
  const [hov, setHov] = useState(false);
  let bg, color, border, borderRadius, padding, fontSize, fontWeight;

  if (variant === 'submit') {
    bg           = hov && !disabled ? t.submitBgColorHover : t.submitBgColor;
    color        = t.submitTextColor;
    border       = 'none';
    borderRadius = t.submitBorderRadius;
    padding      = `${t.submitPaddingY}px ${t.submitPaddingX}px`;
    fontSize     = t.submitFontSize;
    fontWeight   = t.submitFontWeight;
  } else if (variant === 'cancel') {
    const cs = t.cancelButtonStyle || 'ghost';
    borderRadius = t.cancelButtonBorderRadius;
    padding      = `${t.cancelButtonPaddingY || 8}px ${t.cancelButtonPaddingX || 16}px`;
    fontSize     = t.cancelButtonFontSize || 13;
    fontWeight   = t.cancelButtonFontWeight || 500;
    color        = t.cancelButtonTextColor;
    if (cs === 'filled') {
      bg     = hov ? t.submitBgColorHover : (t.cancelButtonBgColor || '#e2e8f0');
      border = 'none';
    } else if (cs === 'outlined') {
      bg     = hov ? 'rgba(0,0,0,0.04)' : 'transparent';
      border = `1px solid ${t.cancelButtonBorderColor || '#e2e8f0'}`;
    } else if (cs === 'link') {
      bg     = 'transparent';
      border = 'none';
    } else { // ghost (default)
      bg     = hov ? 'rgba(0,0,0,0.04)' : 'transparent';
      border = `1px solid ${t.cancelButtonBorderColor || '#e2e8f0'}`;
    }
  } else { // nav (Back / Next-not-last / Skip)
    const style = t.navButtonStyle || 'filled';
    borderRadius = t.navButtonBorderRadius;
    padding      = `${t.navButtonPaddingY || 8}px ${t.navButtonPaddingX || 18}px`;
    fontSize     = t.navButtonFontSize || 13;
    fontWeight   = t.navButtonFontWeight || 500;
    color        = t.navButtonTextColor;
    if (style === 'outlined') {
      bg     = hov ? 'rgba(0,0,0,0.04)' : 'transparent';
      border = `1px solid ${t.navButtonTextColor}`;
    } else if (style === 'ghost') {
      bg     = hov ? 'rgba(0,0,0,0.04)' : 'transparent';
      border = 'none';
    } else { // filled
      bg     = hov && !disabled ? (t.navButtonHoverBgColor || '#e2e8f0') : t.navButtonBgColor;
      border = 'none';
    }
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: bg, color, border, borderRadius, padding, fontSize, fontWeight,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        boxShadow: variant === 'submit' ? shadowVal(t.submitShadow) : 'none',
        width: variant === 'submit' && t.submitFullWidth ? '100%' : 'auto',
        transition: `background ${t.inputTransitionDuration || 150}ms ease, opacity 0.15s`,
        ...extra,
      }}>
      {label}
    </button>
  );
}

// ─── Validation helper ────────────────────────────────────────────────────────

/** Acts on successDestination — the only part of it that needs a browser. */
function runSuccessNavigation(cfg, body) {
  const target = successDestination(cfg, body);
  if (!target) return;
  if (target.delayMs > 0) setTimeout(() => { window.location.href = target.url; }, target.delayMs);
  else window.location.href = target.url;
}

/** The on_success button, styled as the form's own submit button. */
function SuccessLinkButton({ link, tokens: t }) {
  if (!link) return null;
  return (
    <a href={link.url} style={{
      display: 'inline-block', marginTop: 16, textDecoration: 'none',
      background: t.submitBgColor, color: t.submitTextColor,
      borderRadius: t.submitBorderRadius, padding: `${t.submitPaddingY}px ${t.submitPaddingX}px`,
      fontSize: t.submitFontSize, fontWeight: t.submitFontWeight,
    }}>
      {link.label}
    </a>
  );
}

function validateFields(fields, values) {
  const errors = {};
  fields.forEach(f => {
    if (f.required && !values[f.id] && values[f.id] !== false) {
      errors[f.id] = `${f.label} is required`;
    }
    if (f.type === 'email' && values[f.id]) {
      // Shape first, then the allowlist. An address that is not an address has
      // no domain worth naming, and two complaints about one box is one too many.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[f.id])) errors[f.id] = 'Enter a valid email address';
      else {
        const wrongDomain = domainError(f, values[f.id]);
        if (wrongDomain) errors[f.id] = wrongDomain;
      }
    }
    if (!errors[f.id]) {
      const failed = ruleError(f, values[f.id]);
      if (failed) errors[f.id] = failed;
    }
  });
  return errors;
}

// ─── Multi-stage form body ────────────────────────────────────────────────────

function StagedFormBody({ config, tokens, onSuccess, sectionUid, pageVisibility, prefill }) {
  const cfg    = config;
  const t      = tokens;
  const stages = cfg.stages || [];

  const behavior      = cfg.submission_behavior     || 'always_new';
  const postBehavior  = cfg.post_submit_behavior    || 'no_edit';
  const needsAuth     = BEHAVIORS_REQUIRING_AUTH.has(behavior);
  const loggedIn      = isUserLoggedIn();
  const isPrivate     = pageVisibility === 'private';

  const [currentStage, setCurrentStage] = useState(0);
  // const [values, setValues]             = useState({});
  // Same opening answers as the simple body, for parity -- a staged form is
  // still a form somebody may arrive at with half of it already known.
  const [values, setValues]             = useState(() => ({ ...(prefill || {}) }));
  const [errors, setErrors]             = useState({});
  const [completedStages, setCompletedStages] = useState(new Set());
  const [stageSummaries, setStageSummaries]   = useState({});
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [successLink, setSuccessLink]   = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [serverError, setServerError]   = useState('');
  const [showBanner, setShowBanner]     = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [lockedFields, setLockedFields] = useState([]);
  const savedDataRef = useRef(null);

  // ── On mount: load server draft (authenticated) or localStorage draft ──────
  useEffect(() => {
    let cancelled = false;

    async function loadDraft() {
      // A bound form edits a real record rather than a saved copy of the
      // answers. Its stages are just how the questions are laid out, so the
      // record fills them the same way — starting at the first stage, because
      // an edit has no half-finished progress to resume.
      if (loggedIn && cfg.form_id && isBoundForm(cfg)) {
        setDraftLoading(true);
        try {
          const resp = await loadMyRecord(cfg.form_id);
          if (!cancelled && resp.ok) {
            const body = await resp.json();
            if (body.found) {
              setValues(body.values || {});
              setLockedFields(body.locked_fields || []);
            }
          }
        } catch (_) { /* a form nobody has filled in yet is not an error */ }
        finally { if (!cancelled) setDraftLoading(false); }
        return;
      }

      // Server-side draft for any authenticated user on a multi-stage form.
      // needsAuth behaviors also benefit here; always_new only restores in-progress
      // drafts — a completed submission is left alone so the form starts fresh.
      if (loggedIn && cfg.form_id) {
        setDraftLoading(true);
        try {
          const resp = await loadMySubmission(cfg.form_id);
          if (!cancelled && resp.ok) {
            const body = await resp.json();
            if (body.found) {
              // For always_new: only restore if the record is still in draft state.
              // A submitted record means the user already finished — start a new form.
              if (behavior === 'always_new' && body.status !== 'draft') {
                // fall through to localStorage
              } else {
                setValues(body.data || {});
                setCurrentStage(Math.min(body.stage_reached || 0, stages.length - 1));
                if (body.submission_id) setSubmissionId(body.submission_id);
                // If it's a completed record and post_submit_behavior allows editing,
                // mark as already-submitted so the form shows the edit UI immediately.
                if (body.status === 'submitted' && postBehavior === 'allow_edit') {
                  setSubmitted(true);
                  setSubmissionId(body.submission_id);
                }
                return; // server draft loaded; skip localStorage
              }
            }
          }
        } catch (_) {
          // Network error — fall through to localStorage
        } finally {
          if (!cancelled) setDraftLoading(false);
        }
      }

      // localStorage draft fallback
      // Private pages: always restore silently — user is authenticated and
      // expects to continue from where they left off after a refresh.
      if (isPrivate) {
        const saved = loadSavedProgress(cfg);
        if (saved) {
          setValues(saved.data);
          setCurrentStage(Math.min(saved.stage, stages.length - 1));
        }
        return;
      }
      if (!cfg.persistenceEnabled || !cfg.restoreOnLoad) return;
      const saved = loadSavedProgress(cfg);
      if (!saved) return;
      savedDataRef.current = saved;
      if (cfg.restoreBannerVisible !== false) {
        setShowBanner(true);
      } else {
        setValues(saved.data);
        setCurrentStage(Math.min(saved.stage, stages.length - 1));
      }
    }

    loadDraft();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Read-only fields hold their fixed value through restores and resets too.
  useEffect(() => {
    setValues(v => withFixedValues(getAllFields(cfg), v));
  }, [values]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((fieldId, value) => {
    setValues(v => ({ ...v, [fieldId]: value }));
    setErrors(e => { const n = { ...e }; delete n[fieldId]; return n; });
  }, []);

  const handleContinue = () => {
    const saved = savedDataRef.current;
    if (!saved) return;
    setValues(saved.data);
    setCurrentStage(Math.min(saved.stage, stages.length - 1));
    setShowBanner(false);
  };

  const handleStartOver = () => {
    clearProgress(cfg);
    savedDataRef.current = null;
    setShowBanner(false);
  };

  // ── Auto-save draft after each stage ──────────────────────────────────────
  const saveDraftAfterStage = useCallback(async (nextStageIdx, currentValues) => {
    // localStorage — always save on private pages; respect persistenceEnabled elsewhere
    if (isPrivate || cfg.persistenceEnabled) {
      saveProgress(cfg, currentValues, nextStageIdx);
    }
    // Server-side draft for any authenticated user with a form_id.
    // This ensures progress survives page refreshes on private pages (where the
    // user is always logged in) and for logged-in users on public pages too.
    if (loggedIn && cfg.form_id) {
      try {
        await saveMyDraft(cfg.form_id, currentValues, nextStageIdx);
      } catch (_) { /* silent — localStorage already saved */ }
    }
  }, [cfg, loggedIn, isPrivate]);

  const handleNext = async () => {
    const stage = stages[currentStage];
    const stageFields = getFieldsFromRows(stage.rows);
    const newErrors = validateFields(stageFields, values);
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

    await saveDraftAfterStage(currentStage + 1, values);

    const newCompleted = new Set(completedStages);
    newCompleted.add(currentStage);
    setCompletedStages(newCompleted);

    if (stage.summaryOnComplete) {
      setStageSummaries(s => ({ ...s, [currentStage]: true }));
    }

    setErrors({});
    setCurrentStage(i => i + 1);
  };

  const handleBack = () => {
    setCurrentStage(i => Math.max(i - 1, 0));
    setErrors({});
  };

  const handleSkip = () => {
    setCurrentStage(i => Math.min(i + 1, stages.length - 1));
  };

  const handleJump = (stageIdx) => {
    if (completedStages.has(stageIdx) || stageIdx < currentStage) {
      setCurrentStage(stageIdx);
      setErrors({});
    }
  };

  const handleCancel = () => {
    const action = cfg.cancelAction || 'clear-form';
    if (action === 'confirm-then-clear') { setConfirmCancel(true); return; }
    executeCancelAction(action);
  };

  const executeCancelAction = (action) => {
    if (action === 'redirect' && cfg.cancelRedirectUrl) {
      window.location.href = cfg.cancelRedirectUrl; return;
    }
    setValues({});
    setErrors({});
    if (action === 'clear-and-reset-stage') {
      setCurrentStage(0);
      setCompletedStages(new Set());
      setStageSummaries({});
    }
    if (cfg.clearPersistenceOnCancel) clearProgress(cfg);
    setConfirmCancel(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const stage = stages[currentStage];
    const stageFields = getFieldsFromRows(stage.rows);
    const newErrors = validateFields(stageFields, values);
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

    if (!cfg.form_id) { setServerError('Form is not configured correctly.'); return; }

    // If behavior needs auth but user isn't logged in, redirect to login.
    // For popup mode include ?open_form=<sectionUid> so it reopens after login.
    if (needsAuth && !loggedIn) {
      goToSignIn(sectionUid);
      return;
    }

    setSubmitting(true);
    setServerError('');
    try {
      const resp = await submitFormData(`/arcee/public/form/${cfg.form_id}/submit/`, { data: values });
      const body = await resp.json();
      if (!resp.ok) {
        const failure = readSubmitFailure(body);
        if (failure.requiresAuth) { goToSignIn(sectionUid); return; }
        if (failure.fieldErrors) {
          setErrors(failure.fieldErrors);
          // A staged form only renders the current stage, so an error against a
          // field on an earlier one would be set and never seen — the form just
          // appeared to ignore Submit. Go to the stage that can show it.
          const stageIdx = stageHoldingFirstError(
            stages.map(st => getFieldsFromRows(st.rows).map(f => f.id)),
            failure.fieldErrors,
          );
          if (stageIdx >= 0 && stageIdx !== currentStage) {
            setCurrentStage(stageIdx);
            setServerError('Please check the highlighted answers.');
          }
        }
        if (failure.message) setServerError(failure.message);
        return;
      }
      if (isPrivate || cfg.persistenceEnabled) clearProgress(cfg);
      setSubmissionId(body.submission_id || null);
      setSubmitted(true);
      if (onSuccess) onSuccess();
      runSuccessNavigation(cfg, body);
      setSuccessLink(successButton(cfg, body));
    } catch (err) {
      // A dead session is not a network problem, and telling someone to "try
      // again" cannot fix it. Send them to sign in and bring them back.
      if (isSessionGone(err)) { goToSignIn(sectionUid); return; }
      setServerError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditAfterSubmit = () => {
    setSubmitted(false);
  };

  if (draftLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
        <span className="spinner-border" style={{ color: t.inputFocusRingColor }} />
      </div>
    );
  }

  if (submitted) {
    if (postBehavior === 'show_completed') {
      return (
        <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
          <i className="bi bi-lock-fill" style={{ fontSize: 48, color: '#64748b' }} />
          <p style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: '1rem', color: t.inputTextColor }}>
            {cfg.completed_message || cfg.success_message || 'Submission complete.'}
          </p>
        </div>
      );
    }
    if (postBehavior === 'allow_edit') {
      return (
        <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
          <i className="bi bi-check-circle-fill" style={{ fontSize: 52, color: t.successColor }} />
          <p style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: '1rem', color: t.inputTextColor }}>
            {cfg.success_message || 'Thank you!'}
          </p>
          <SuccessLinkButton link={successLink} tokens={t} />
          <button type="button" onClick={handleEditAfterSubmit} style={{
            marginTop: 12, background: 'none', border: `1px solid ${t.inputBorderColor}`,
            borderRadius: 6, padding: '6px 18px', fontSize: 13, cursor: 'pointer', color: t.inputTextColor,
          }}>
            <i className="bi bi-pencil me-1" />Edit my submission
          </button>
        </div>
      );
    }
    // Default: no_edit
    return (
      <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
        <i className="bi bi-check-circle-fill" style={{ fontSize: 52, color: t.successColor }} />
        <p style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: '1rem', color: t.inputTextColor }}>
          {cfg.success_message || 'Thank you!'}
        </p>
        <SuccessLinkButton link={successLink} tokens={t} />
      </div>
    );
  }

  const stage = stages[currentStage];
  const isFirstStage = currentStage === 0;
  const isLastStage  = currentStage === stages.length - 1;
  const showProgress = cfg.progressIndicatorStyle !== 'none' && cfg.progressPosition !== 'bottom';
  const showProgressBottom = cfg.progressIndicatorStyle !== 'none' && cfg.progressPosition === 'bottom';

  const progressNode = (
    <ProgressIndicator
      stages={stages}
      currentStage={currentStage}
      cfg={cfg}
      tokens={t}
      completedStages={completedStages}
      onJump={handleJump}
    />
  );

  return (
    <form onSubmit={isLastStage ? handleSubmit : e => { e.preventDefault(); handleNext(); }} noValidate>
      {showBanner && (
        <RestoreBanner cfg={cfg} tokens={t} onContinue={handleContinue} onStartOver={handleStartOver} />
      )}

      {cfg.form_title && (
        <h3 style={{ fontWeight: 700, marginBottom: '1rem', color: t.inputTextColor }}>
          {cfg.form_title}
        </h3>
      )}

      {showProgress && progressNode}

      {/* Stage summaries for previously completed stages */}
      {stages.slice(0, currentStage).map((s, i) =>
        stageSummaries[i] ? (
          <StageSummaryCard key={s.stageId || i} stage={s} values={values} cfg={cfg} tokens={t}
            onEdit={() => handleJump(i)} />
        ) : null
      )}

      {stage?.stageLabel && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.inputTextColor }}>{stage.stageLabel}</div>
          {stage.stageDescription && (
            <div style={{ fontSize: 13, color: t.helpTextColor, marginTop: 4 }}>{stage.stageDescription}</div>
          )}
        </div>
      )}

      {serverError && <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{serverError}</div>}

      <RowGrid key={`stage-${currentStage}`} rows={stage?.rows || []} values={values} onChange={handleChange} errors={errors} tokens={t} formId={cfg.form_id} lockedFields={lockedFields} />

      {showProgressBottom && progressNode}

      {/* Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, gap: 8, flexWrap: 'wrap' }}>
        {/* Left side: Back + Cancel (if left/inline position) */}
        <div style={{ display: 'flex', gap: 8 }}>
          {!isFirstStage && (
            <NavBtn label={`← ${stage?.backLabel || cfg.defaultBackLabel || 'Back'}`} onClick={handleBack} tokens={t} />
          )}
          {cfg.cancelButtonVisible && cfg.cancelPosition !== 'right' && (
            <NavBtn label={cfg.cancelLabel || 'Cancel'} onClick={handleCancel} tokens={t} variant="cancel" />
          )}
        </div>

        {/* Right side: Skip + Next/Submit + Cancel (if right) */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {stage?.allowSkip && !isLastStage && (
            <NavBtn label={stage.skipLabel || cfg.defaultSkipLabel || 'Skip'} onClick={handleSkip} tokens={t} variant="cancel" />
          )}
          {isLastStage ? (
            <NavBtn type="submit" label={submitting ? 'Submitting…' : (cfg.button_text || 'Submit')} onClick={null} variant="submit" disabled={submitting} style={{ cursor: 'pointer' }}
              tokens={{ ...t, submitBgColor: cfg.button_color || t.submitBgColor, submitTextColor: cfg.button_text_color || t.submitTextColor, submitFullWidth: cfg.submit_full_width || t.submitFullWidth }} />
          ) : (
            <NavBtn label={`${stage?.nextLabel || cfg.defaultNextLabel || (isPrivate ? 'Save & Next' : 'Next')} →`} onClick={handleNext} tokens={t} variant="submit" />
          )}
          {cfg.cancelButtonVisible && cfg.cancelPosition === 'right' && (
            <NavBtn label={cfg.cancelLabel || 'Cancel'} onClick={handleCancel} tokens={t} variant="cancel" />
          )}
        </div>
      </div>

      {/* Cancel confirmation dialog */}
      {confirmCancel && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '24px', maxWidth: 400, width: '100%', margin: 16 }}>
            <p style={{ fontSize: 14, color: '#1e293b', marginBottom: 20 }}>
              {cfg.cancelConfirmMessage || 'Are you sure you want to cancel?'}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmCancel(false)}
                style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13 }}>
                {cfg.cancelConfirmNoLabel || 'No, continue filling'}
              </button>
              <button type="button" onClick={() => executeCancelAction('clear-form')}
                style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {cfg.cancelConfirmYesLabel || 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

// ─── Simple (non-staged) form body ────────────────────────────────────────────

function SimpleFormBody({ config, tokens, onSuccess, sectionUid, prefill }) {
  const cfg = config;
  const t   = tokens;

  const behavior     = cfg.submission_behavior  || 'always_new';
  const postBehavior = cfg.post_submit_behavior || 'no_edit';
  const needsAuth    = BEHAVIORS_REQUIRING_AUTH.has(behavior);
  const loggedIn     = isUserLoggedIn();

  const allFields = getAllFields(cfg);
  // const [values, setValues]         = useState({});
  // A caller may hand the form its opening answers (see `prefill` on the main
  // export). Lazy initialiser, so the spread runs once on mount and not on
  // every render. WITH NO `prefill` THIS IS `{}` -- byte for byte the empty
  // form every existing call site has always rendered.
  const [values, setValues]         = useState(() => ({ ...(prefill || {}) }));
  const [errors, setErrors]         = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [successLink, setSuccessLink] = useState(null);
  const [serverError, setServerError] = useState('');
  const [btnHov, setBtnHov]         = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [lockedFields, setLockedFields] = useState([]);

  // Load existing submission for authenticated users with relevant behaviors
  useEffect(() => {
    let cancelled = false;
    async function loadExisting() {
      if (!loggedIn || !cfg.form_id) return;

      // A bound form edits a real record, not a saved copy of the answers, so
      // it reads the record instead. Which record is theirs is decided by the
      // server from the session.
      if (isBoundForm(cfg)) {
        setDraftLoading(true);
        try {
          const resp = await loadMyRecord(cfg.form_id);
          if (!cancelled && resp.ok) {
            const body = await resp.json();
            if (body.found) {
              setValues(body.values || {});
              setLockedFields(body.locked_fields || []);
            }
          }
        } catch (_) { /* a form nobody has filled in yet is not an error */ }
        finally { if (!cancelled) setDraftLoading(false); }
        return;
      }

      if (!needsAuth) return;
      setDraftLoading(true);
      try {
        const resp = await loadMySubmission(cfg.form_id);
        if (!cancelled && resp.ok) {
          const body = await resp.json();
          if (body.found) {
            setValues(body.data || {});
            if (body.status === 'submitted' && postBehavior === 'allow_edit') {
              setSubmitted(true);
            }
          }
        }
      } catch (_) { /* ignore */ } finally {
        if (!cancelled) setDraftLoading(false);
      }
    }
    loadExisting();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Read-only fields hold their fixed value through restores and resets too.
  useEffect(() => {
    setValues(v => withFixedValues(getAllFields(cfg), v));
  }, [values]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((fieldId, value) => {
    setValues(v => ({ ...v, [fieldId]: value }));
    setErrors(e => { const n = { ...e }; delete n[fieldId]; return n; });
  }, []);

  // ── Repeated blocks ────────────────────────────────────────────────────
  const groups = repeatGroups(cfg);
  const repeated = repeatedFieldIds(cfg);
  const fieldsById = Object.fromEntries(allFields.map(f => [f.id, f]));

  const handleCopyChange = useCallback((fieldId, index, value) => {
    setValues(v => setCopyValue(v, fieldId, index, value));
    setErrors(e => { const n = { ...e }; delete n[`${fieldId}#${index}`]; return n; });
  }, []);

  const handleAddCopy = useCallback((group) => {
    setValues(v => {
      const at = copyCount(group, v);
      let next = v;
      group.fieldIds.forEach(id => { next = setCopyValue(next, id, at, ''); });
      return next;
    });
  }, []);

  const handleRemoveCopy = useCallback((group, index) => {
    setValues(v => removeCopy(v, group, index));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Repeated answers are validated per copy, not as one value — a required
    // question is required in each copy the person opened.
    const newErrors = validateFields(allFields.filter(f => !repeated.has(f.id)), values);
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    if (!cfg.form_id) { setServerError('Form is not configured correctly.'); return; }

    if (needsAuth && !loggedIn) {
      goToSignIn(sectionUid);
      return;
    }

    setSubmitting(true);
    setServerError('');
    try {
      const resp = await submitFormData(`/arcee/public/form/${cfg.form_id}/submit/`, { data: values });
      const body = await resp.json();
      if (!resp.ok) {
        const failure = readSubmitFailure(body);
        if (failure.requiresAuth) { goToSignIn(sectionUid); return; }
        if (failure.fieldErrors) setErrors(failure.fieldErrors);
        if (failure.message) setServerError(failure.message);
        return;
      }
      setSubmitted(true);
      if (onSuccess) onSuccess();
      runSuccessNavigation(cfg, body);
      setSuccessLink(successButton(cfg, body));
    } catch (err) {
      // A dead session is not a network problem, and telling someone to "try
      // again" cannot fix it. Send them to sign in and bring them back.
      if (isSessionGone(err)) { goToSignIn(sectionUid); return; }
      setServerError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditAfterSubmit = () => setSubmitted(false);

  if (draftLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
        <span className="spinner-border" style={{ color: t.inputFocusRingColor }} />
      </div>
    );
  }

  if (submitted) {
    if (postBehavior === 'show_completed') {
      return (
        <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
          <i className="bi bi-lock-fill" style={{ fontSize: 48, color: '#64748b' }} />
          <p style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: '1rem', color: t.inputTextColor }}>
            {cfg.completed_message || cfg.success_message || 'Submission complete.'}
          </p>
        </div>
      );
    }
    if (postBehavior === 'allow_edit') {
      return (
        <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
          <i className="bi bi-check-circle-fill" style={{ fontSize: 52, color: t.successColor }} />
          <p style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: '1rem', color: t.inputTextColor }}>
            {cfg.success_message || 'Thank you!'}
          </p>
          <SuccessLinkButton link={successLink} tokens={t} />
          <button type="button" onClick={handleEditAfterSubmit} style={{
            marginTop: 12, background: 'none', border: `1px solid ${t.inputBorderColor}`,
            borderRadius: 6, padding: '6px 18px', fontSize: 13, cursor: 'pointer', color: t.inputTextColor,
          }}>
            <i className="bi bi-pencil me-1" />Edit my submission
          </button>
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
        <i className="bi bi-check-circle-fill" style={{ fontSize: 52, color: t.successColor }} />
        <p style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: '1rem', color: t.inputTextColor }}>
          {cfg.success_message || 'Thank you!'}
        </p>
        <SuccessLinkButton link={successLink} tokens={t} />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {cfg.form_title && (
        <h3 style={{ fontWeight: 700, marginBottom: '1.25rem', color: t.inputTextColor }}>{cfg.form_title}</h3>
      )}
      {serverError && (
        <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{serverError}</div>
      )}

      {cfg.rowLayoutEnabled && (cfg.rows || []).length > 0 ? (
        <RowGrid
          rows={(cfg.rows || []).map(r => ({
            ...r, fields: (r.fields || []).filter(f => !repeated.has(f.id)),
          }))}
          values={values} onChange={handleChange} errors={errors} tokens={t}
          formId={cfg.form_id} lockedFields={lockedFields} />
      ) : (
        <div>
          {(cfg.fields || [])
            .filter(field => !repeated.has(field.id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(field => (
            <div key={field.id} style={{ marginBottom: t.fieldGap }}>
              <FormField field={field} value={values[field.id]} onChange={handleChange} tokens={t} error={errors[field.id]} formId={cfg.form_id} values={values} locked={lockedFields.includes(field.id)} />
            </div>
          ))}
        </div>
      )}

      {groups.map(group => (
        <RepeatBlock
          key={group.key}
          group={group}
          fields={group.fieldIds.map(id => fieldsById[id]).filter(Boolean)}
          values={values}
          onCopyChange={handleCopyChange}
          onAddCopy={handleAddCopy}
          onRemoveCopy={handleRemoveCopy}
          errors={errors}
          tokens={t}
          formId={cfg.form_id}
        />
      ))}

      <button type="submit" disabled={submitting}
        onMouseEnter={() => setBtnHov(true)} onMouseLeave={() => setBtnHov(false)}
        style={{
          marginTop: 8,
          background: btnHov && !submitting
            ? (cfg.button_color || t.submitBgColorHover)
            : (cfg.button_color || t.submitBgColor),
          color: cfg.button_text_color || t.submitTextColor,
          border: 'none',
          borderRadius: t.submitBorderRadius,
          padding: `${t.submitPaddingY}px ${t.submitPaddingX}px`,
          fontSize: t.submitFontSize, fontWeight: t.submitFontWeight,
          cursor: submitting ? 'not-allowed' : 'pointer',
          opacity: submitting ? 0.7 : 1,
          width: (cfg.submit_full_width || t.submitFullWidth) ? '100%' : 'auto',
          boxShadow: shadowVal(t.submitShadow),
          transition: `background ${t.inputTransitionDuration || 150}ms ease`,
        }}>
        {submitting
          ? <><span className="spinner-border spinner-border-sm me-2" />{cfg.button_text || 'Submit'}…</>
          : (cfg.button_text || 'Submit')}
      </button>
    </form>
  );
}

// ─── Popup modal ──────────────────────────────────────────────────────────────

const POPUP_WIDTH = { sm: 400, md: 540, lg: 720, xl: 900 };

function PopupModal({ config, tokens, onClose, sectionUid, pageVisibility, prefill }) {
  const t   = tokens;
  const maxW = POPUP_WIDTH[config.popup_width] || 540;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1050,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backdropFilter: 'blur(2px)',
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: t.formBgColor, borderRadius: t.formBorderRadius,
        padding: '2rem', maxWidth: maxW, width: '100%', maxHeight: '90vh',
        overflowY: 'auto', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <button type="button" onClick={onClose} style={{
          position: 'absolute', top: 14, right: 14, background: '#f1f5f9', border: 'none',
          borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer', color: '#475569', fontSize: 14,
        }}>
          <i className="bi bi-x-lg" />
        </button>
        {config.stagesEnabled && (config.stages || []).length > 0
          ? <StagedFormBody config={config} tokens={tokens} onSuccess={onClose} sectionUid={sectionUid} pageVisibility={pageVisibility} prefill={prefill} />
          : <SimpleFormBody config={config} tokens={tokens} onSuccess={onClose} sectionUid={sectionUid} prefill={prefill} />}
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function FormBuilderSection({ config, pageVisibility, prefill }) {
  /*
   * `prefill` -- OPTIONAL opening answers, keyed by field id, for a caller that
   * already knows some of them. My Transactions uses it to open the Contact Us
   * form with the subject and the payment's details written out (see
   * PaymentSupportModal).
   *
   * ADDITIVE AND INERT BY DEFAULT. A page-builder page never passes it, so
   * `values` starts `{}` exactly as before and every form rendered from a page
   * behaves as it always has. It only ever SEEDS the first render: the person
   * can edit or clear any of it, and nothing here locks a field.
   */
  const cfg = config || {};
  const tokens = resolveTokens(cfg);
  const uid = useId().replace(/:/g, '');
  const scopeClass  = `arcee-fb-${uid}`;
  const sectionUid  = uid;

  const displayMode = cfg.display_mode || 'inline';
  const isStaged    = cfg.stagesEnabled && (cfg.stages || []).length > 0;

  // Popup: auto-open when URL contains ?open_form=<this section's uid>.
  // This handles the post-login redirect flow for auth-required forms on public pages:
  // 1. User clicks Submit without being logged in.
  // 2. Client redirects to /sign-in?next=<currentPath>?open_form=<sectionUid>.
  // 3. After login SignInPage navigates to the ?next= URL.
  // 4. On page load this effect detects ?open_form=<sectionUid> and opens the popup.
  // 5. The form's useEffect calls GET /my-submission/ and restores the server draft.
  const shouldAutoOpen = displayMode === 'popup' && (() => {
    try {
      return new URLSearchParams(window.location.search).get('open_form') === sectionUid;
    } catch (_) { return false; }
  })();

  const [popupOpen, setPopupOpen] = useState(shouldAutoOpen);

  const wrapperStyle = outerContainerStyle(cfg);
  const cardStyle    = formCardStyle(cfg, tokens);

  const t = tokens;
  const placeholderCss = (() => {
    const rules = [
      `color: ${t.inputPlaceholderColor}`,
      ...(t.inputPlaceholderFontSize   ? [`font-size: ${t.inputPlaceholderFontSize}px`]                : []),
      ...(t.inputPlaceholderFontWeight ? [`font-weight: ${t.inputPlaceholderFontWeight}`]               : []),
      ...(t.inputPlaceholderFontFamily ? [`font-family: ${t.inputPlaceholderFontFamily}`]               : []),
      ...(t.inputPlaceholderFontStyle && t.inputPlaceholderFontStyle !== 'normal'
                                       ? [`font-style: ${t.inputPlaceholderFontStyle}`]                 : []),
    ];
    return [
      `.${scopeClass} input::placeholder, .${scopeClass} textarea::placeholder { ${rules.join('; ')} }`,
      // Used by the file field while its upload is in flight.
      '@keyframes arcee-spin { to { transform: rotate(360deg); } }',
      '@media (prefers-reduced-motion: reduce) { .arcee-spin, [style*="arcee-spin"] { animation: none !important; } }',
    ].join('\n');
  })();

  if (displayMode === 'popup') {
    const trigColor = cfg.trigger_button_color || 'var(--primary-color)';
    const trigStyle = cfg.trigger_button_style || 'filled';
    const trigAlign = cfg.trigger_button_align || 'left';
    const trigText  = cfg.trigger_button_text  || 'Open Form';
    const trigIcon  = cfg.trigger_button_icon;

    const btnStyle = (() => {
      const base = { cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', borderRadius: 8, padding: '0.6rem 2rem', display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'opacity 0.15s' };
      const c = trigColor;
      if (trigStyle === 'outline') return { ...base, background: 'transparent', color: c, border: `2px solid ${c}` };
      if (trigStyle === 'ghost')   return { ...base, background: 'transparent', color: c, border: 'none' };
      return { ...base, background: c, color: '#fff', border: 'none' };
    })();
    const wrapAlign = { left: 'flex-start', center: 'center', right: 'flex-end' }[trigAlign] || 'flex-start';

    return (
      <>
        <div style={{ display: 'flex', justifyContent: wrapAlign }}>
          <button type="button" style={btnStyle} onClick={() => setPopupOpen(true)}>
            {trigIcon && <i className={`bi ${trigIcon}`} />}{trigText}
          </button>
        </div>
        {popupOpen && (
          <PopupModal
            config={cfg}
            tokens={tokens}
            sectionUid={sectionUid}
            pageVisibility={pageVisibility}
            prefill={prefill}
            onClose={() => setPopupOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <div style={wrapperStyle} className={scopeClass}>
      <style dangerouslySetInnerHTML={{ __html: placeholderCss }} />
      <div style={cardStyle}>
        {isStaged
          ? <StagedFormBody config={cfg} tokens={tokens} sectionUid={sectionUid} pageVisibility={pageVisibility} prefill={prefill} />
          : <SimpleFormBody config={cfg} tokens={tokens} sectionUid={sectionUid} prefill={prefill} />}
      </div>
    </div>
  );
}

// ─── Hex to RGB string helper ─────────────────────────────────────────────────

function hexToRgbStr(hex) {
  if (!hex || !hex.startsWith('#')) return '255,255,255';
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '255,255,255';
  return `${r},${g},${b}`;
}
